import { Topic, Scripture, Idea, StudyPath, KnowledgeGraphData } from '../types';

export const INITIAL_TOPICS: Topic[] = [
  {
    id: 'top-1',
    name: 'Messiah',
    slug: 'messiah',
    hebrewName: 'מָשִׁיחַ',
    hebrewMeaning: 'Anointed One / Mashiach',
    description: 'Explore biblical prophecies, kingship, the suffering servant, and Yeshua as the fulfillment of Israel’s hope.',
    isFeatured: true,
  },
  {
    id: 'top-2',
    name: 'Torah',
    slug: 'torah',
    hebrewName: 'תּוֹרָה',
    hebrewMeaning: 'Instruction / Teaching / Guidance',
    description: 'Understand Torah not as cold legalism, but as God’s loving instruction for covenant life and kingdom righteousness.',
    isFeatured: true,
  },
  {
    id: 'top-3',
    name: 'Prophecy',
    slug: 'prophecy',
    hebrewName: 'נְבוּאָה',
    hebrewMeaning: 'Prophetic Proclamation / Nevuah',
    description: 'Trace the promises spoken through the prophets from the Tanakh into the Apostolic Scriptures.',
    isFeatured: true,
  },
  {
    id: 'top-4',
    name: 'Covenant',
    slug: 'covenant',
    hebrewName: 'בְּרִית',
    hebrewMeaning: 'Berit / Sacred Binding Bond',
    description: 'From Noah and Abraham to Sinai, David, and the Promised New Covenant established in Yeshua’s blood.',
    isFeatured: true,
  },
  {
    id: 'top-5',
    name: 'Kingdom',
    slug: 'kingdom',
    hebrewName: 'מַלְכוּת',
    hebrewMeaning: 'Malkhut Shamayim / Reign of Heaven',
    description: 'The sovereign rule of God breaking into present reality, awaiting full eschatological consummation.',
    isFeatured: false,
  },
  {
    id: 'top-6',
    name: 'Feasts',
    slug: 'feasts',
    hebrewName: 'מוֹעֲדִים',
    hebrewMeaning: 'Moedim / Appointed Times of the Lord',
    description: 'Passover, Shavuot, Yom Kippur, Sukkot — biblical appointments shadowing Messiah’s redemption plan.',
    isFeatured: true,
  },
  {
    id: 'top-7',
    name: 'Sabbath',
    slug: 'sabbath',
    hebrewName: 'שַׁבָּת',
    hebrewMeaning: 'Shabbat / Ceasing & Delight',
    description: 'A weekly foretaste of the World to Come, a covenant sign of liberation and holy rest in Adonai.',
    isFeatured: false,
  },
  {
    id: 'top-8',
    name: 'Prayer',
    slug: 'prayer',
    hebrewName: 'תְּפִלָּה',
    hebrewMeaning: 'Tefillah / Self-evaluation & Connection',
    description: 'From the Shema and the Amidah to the Disciples’ Prayer (Avinu Malkeinu), standing before the Holy One.',
    isFeatured: false,
  },
  {
    id: 'top-9',
    name: 'Jewish Context',
    slug: 'jewish-context',
    hebrewName: 'הֶקְשֵׁר',
    hebrewMeaning: 'Heksher / Historical & Second Temple Setting',
    description: 'Discovering Yeshua, Paul, and the early disciples inside their 1st-century Jewish cultural and linguistic environment.',
    isFeatured: true,
  },
  {
    id: 'top-10',
    name: 'Hebrew Concepts',
    slug: 'hebrew-concepts',
    hebrewName: 'לָשׁוֹן הַקֹּדֶשׁ',
    hebrewMeaning: 'Lashon HaKodesh / The Holy Tongue',
    description: 'Deep Hebrew vocabulary like Hesed (loyal love), Shalom (wholeness), Teshuvah (return), and Da’at (intimate knowledge).',
    isFeatured: false,
  },
];

export const INITIAL_SCRIPTURES: Scripture[] = [
  {
    id: 'sc-1',
    book: 'Isaiah',
    bookAbbreviation: 'Isa',
    testament: 'Tanakh',
    division: 'Prophets',
    chapter: 53,
    verseStart: 3,
    verseEnd: 6,
    reference: 'Isaiah 53:3–6',
    text: 'He was despised and rejected by men, a man of sorrows and acquainted with grief... But He was pierced for our transgressions, He was crushed for our iniquities; upon Him was the chastisement that brought us peace, and with His wounds we are healed.',
    translation: 'ESV / Messianic Reading',
  },
  {
    id: 'sc-2',
    book: 'Exodus',
    bookAbbreviation: 'Exod',
    testament: 'Tanakh',
    division: 'Torah',
    chapter: 12,
    verseStart: 5,
    verseEnd: 13,
    reference: 'Exodus 12:5, 13',
    text: 'Your lamb shall be without blemish, a male a year old... And when I see the blood, I will pass over you, and no plague will befall you to destroy you when I strike the land of Egypt.',
    translation: 'Tanakh / Tree of Life',
  },
  {
    id: 'sc-3',
    book: '1 Corinthians',
    bookAbbreviation: '1Cor',
    testament: 'Apostolic',
    division: 'Epistles',
    chapter: 5,
    verseStart: 7,
    verseEnd: 8,
    reference: '1 Corinthians 5:7–8',
    text: 'Cleanse out the old leaven that you may be a new lump, as you really are unleavened. For Messiah, our Passover lamb, has been sacrificed. Let us therefore celebrate the festival, not with the old leaven... but with the unleavened bread of sincerity and truth.',
    translation: 'TLV (Tree of Life Version)',
  },
  {
    id: 'sc-4',
    book: 'Jeremiah',
    bookAbbreviation: 'Jer',
    testament: 'Tanakh',
    division: 'Prophets',
    chapter: 31,
    verseStart: 31,
    verseEnd: 34,
    reference: 'Jeremiah 31:31–33',
    text: 'Behold, the days are coming, declares the LORD, when I will make a new covenant with the house of Israel and the house of Judah... I will put my law within them, and I will write it on their hearts. And I will be their God, and they shall be my people.',
    translation: 'ESV',
  },
  {
    id: 'sc-5',
    book: 'Deuteronomy',
    bookAbbreviation: 'Deut',
    testament: 'Tanakh',
    division: 'Torah',
    chapter: 8,
    verseStart: 2,
    verseEnd: 3,
    reference: 'Deuteronomy 8:2–3',
    text: 'And you shall remember the whole way that the LORD your God has led you these forty years in the wilderness, that he might humble you, testing you to know what was in your heart... that he might make you know that man does not live by bread alone, but man lives by every word that comes from the mouth of the LORD.',
    translation: 'ESV',
  },
  {
    id: 'sc-6',
    book: 'Matthew',
    bookAbbreviation: 'Matt',
    testament: 'Apostolic',
    division: 'Gospels',
    chapter: 4,
    verseStart: 1,
    verseEnd: 4,
    reference: 'Matthew 4:1–4',
    text: 'Then Yeshua was led up by the Spirit into the wilderness to be tempted by the devil. And after fasting forty days and forty nights, he was hungry... But he answered, "It is written: Man shall not live by bread alone, but by every word that comes from the mouth of God."',
    translation: 'TLV',
  },
  {
    id: 'sc-7',
    book: 'Psalm',
    bookAbbreviation: 'Ps',
    testament: 'Tanakh',
    division: 'Writings',
    chapter: 23,
    verseStart: 1,
    verseEnd: 3,
    reference: 'Psalm 23:1–3',
    text: 'Adonai is my shepherd; I shall not want. He makes me lie down in green pastures; He leads me beside still waters. He restores my soul; He guides me in paths of righteousness for His name’s sake.',
    translation: 'TLV / Tanakh',
  },
  {
    id: 'sc-8',
    book: 'John',
    bookAbbreviation: 'John',
    testament: 'Apostolic',
    division: 'Gospels',
    chapter: 10,
    verseStart: 11,
    verseEnd: 15,
    reference: 'John 10:11, 14',
    text: 'I am the good shepherd. The good shepherd lays down his life for the sheep... I know my own and my own know me, just as the Father knows me and I know the Father; and I lay down my life for the sheep.',
    translation: 'ESV',
  },
  {
    id: 'sc-9',
    book: 'Leviticus',
    bookAbbreviation: 'Lev',
    testament: 'Tanakh',
    division: 'Torah',
    chapter: 23,
    verseStart: 1,
    verseEnd: 3,
    reference: 'Leviticus 23:1–3',
    text: 'The LORD spoke to Moses, saying, "Speak to the people of Israel and say to them, These are the appointed feasts of the LORD that you shall proclaim as holy convocations; they are my appointed feasts. Six days shall work be done, but on the seventh day is a Sabbath of solemn rest."',
    translation: 'ESV',
  },
  {
    id: 'sc-10',
    book: 'Micah',
    bookAbbreviation: 'Mic',
    testament: 'Tanakh',
    division: 'Prophets',
    chapter: 5,
    verseStart: 2,
    verseEnd: 4,
    reference: 'Micah 5:2',
    text: 'But you, O Bethlehem Ephrathah, who are too little to be among the clans of Judah, from you shall come forth for me one who is to be ruler in Israel, whose coming forth is from of old, from ancient days.',
    translation: 'ESV',
  },
  {
    id: 'sc-11',
    book: 'Zechariah',
    bookAbbreviation: 'Zech',
    testament: 'Tanakh',
    division: 'Prophets',
    chapter: 12,
    verseStart: 10,
    verseEnd: 10,
    reference: 'Zechariah 12:10',
    text: 'And I will pour out on the house of David and the inhabitants of Jerusalem a spirit of grace and pleas for mercy, so that, when they look on me, on him whom they have pierced, they shall mourn for him, as one mourns for an only child.',
    translation: 'ESV',
  },
  {
    id: 'sc-12',
    book: 'Deuteronomy',
    bookAbbreviation: 'Deut',
    testament: 'Tanakh',
    division: 'Torah',
    chapter: 6,
    verseStart: 4,
    verseEnd: 7,
    reference: 'Deuteronomy 6:4–7',
    text: 'Hear, O Israel: The LORD our God, the LORD is one. You shall love the LORD your God with all your heart and with all your soul and with all your might. And these words that I command you today shall be on your heart.',
    translation: 'Shema / Tanakh',
  }
];

export const INITIAL_IDEAS: Idea[] = [
  {
    id: 'idea-1',
    title: 'The Suffering Servant & The King',
    slug: 'the-suffering-servant',
    category: 'Messiah',
    hook: 'The Hebrew Scriptures present two seemingly irreconcilable portraits of the Messiah: a reigning Son of David and a pierced Servant.',
    summary: 'In Jewish tradition, these two portraits led ancient sages to speak of two messianic figures: Mashiach ben Yosef (the suffering one) and Mashiach ben David (the triumphant king). Through Yeshua, both threads merge into one divine trajectory across two comings.',
    content: 'Isaiah 52:13–53:12 describes an enigmatic figure called "My Servant" who bears Israel\'s griefs, is pierced for human transgressions, and offers his soul as an Asham (guilt offering). Rather than being merely a symbolic portrait of corporate Israel, the text emphasizes his substitutionary role on behalf of the nation.',
    context: 'In the Second Temple period and the Babylonian Talmud (Sanhedrin 98b), this passage was frequently identified with the Messiah known as "the Leprous Scholar." Ancient Targum Jonathan explicitly opens Isaiah 52:13 with: "Behold, My servant the Messiah shall prosper."',
    interpretation: 'A Messianic Jewish perspective recognizes that Yeshua first came in the role of the Suffering Servant to secure internal spiritual redemption from sin, and will return in majesty as the reigning Son of David to establish the Messianic Kingdom on earth.',
    application: 'When facing personal trials or persecution for faith, we are anchored by the reality that our King first endured rejection and sorrow, transforming suffering into redemptive purpose.',
    status: 'PUBLISHED',
    featured: true,
    createdBy: 'Editorial Team',
    reviewedBy: 'Rabbinic Reviewer',
    publishedAt: '2026-09-01T00:00:00Z',
    createdAt: '2026-08-15T10:00:00Z',
    updatedAt: '2026-09-01T12:00:00Z',
    scriptureIds: ['sc-1', 'sc-11'],
    topicSlugs: ['messiah', 'prophecy', 'covenant'],
    readTimeMinutes: 3,
  },
  {
    id: 'idea-2',
    title: 'The Passover Lamb & Eternal Deliverance',
    slug: 'the-passover-lamb',
    category: 'Feasts',
    hook: 'The Exodus was never meant to be just an ancient memory; it is the master template for Messiah’s cosmic redemption.',
    summary: 'Every Passover Seder commands each generation to view themselves as if they personally went out of Egypt. The blood of the spotless lamb applied to the doorposts foreshadowed the ultimate atonement of Yeshua, who Paul explicitly calls "Messiah our Passover."',
    content: 'In Exodus 12, Israel was delivered not by military might or philosophical enlightenment, but through the sacrificial blood of an unblemished yearling lamb. The blood was marked on the lintel and two doorposts — forming three points reminiscent of the tree of execution. Death passed over every household under the sign of the blood.',
    context: 'During the 1st-century Passover in Jerusalem, hundreds of thousands of lambs were brought to the Temple. At precisely 3:00 PM (the time of the Minchah offering), the high priest would proclaim "It is finished" as the final Passover sacrifice was offered. Yeshua uttered these exact words upon the stake at that very hour.',
    interpretation: 'Messianic understanding connects the Afikoman (the middle matzah broken, wrapped in linen, hidden away, and later redeemed by the children) with Yeshua’s broken body, burial, and glorious resurrection on the third day.',
    application: 'We celebrate our freedom from the slavery of sin and death not through our own righteousness, but by abiding under the protective shelter of the Lamb’s covenant blood.',
    status: 'PUBLISHED',
    featured: true,
    createdBy: 'Editorial Team',
    reviewedBy: 'Theological Board',
    publishedAt: '2026-09-05T00:00:00Z',
    createdAt: '2026-08-20T10:00:00Z',
    updatedAt: '2026-09-05T12:00:00Z',
    scriptureIds: ['sc-2', 'sc-3'],
    topicSlugs: ['feasts', 'messiah', 'covenant'],
    readTimeMinutes: 4,
  },
  {
    id: 'idea-3',
    title: 'The Wilderness as Divine Testing & Preparation',
    slug: 'the-wilderness',
    category: 'Jewish Context',
    hook: 'The wilderness was never just a desert path; throughout Scripture it is God’s sacred crucible for dependence and encounter.',
    summary: 'When God brought Israel into the Midbar (wilderness), it was not because He lacked a direct road to Canaan. It was to strip away Egyptian identity and cultivate radical trust: that man does not live by bread alone, but by every word proceeding from Adonai.',
    content: 'Deuteronomy 8 recounts forty years of divine testing. God allowed hunger and provided heavenly Manna so Israel would recognize that earthly sustenance is contingent upon divine faithfulness. When Yeshua entered the Judean wilderness for 40 days, He faced the adversary as Israel’s faithful representative, answering every temptation with words from Deuteronomy.',
    context: 'In Hebrew thought, "Midbar" (wilderness) shares its root with "Dabar" (word / to speak). The wilderness is geographically silent so that the voice of the Shepherd can be heard without distraction.',
    interpretation: 'Whereas Israel stumbled through murmuring in their 40-year wilderness trial, Yeshua triumphed in His 40-day fast. He demonstrated that true life is found in total submission to the Father\'s commandments.',
    application: 'If you feel in a dry spiritual season, do not assume God has abandoned you. The wilderness is the place where God builds character and purifies desire for His presence.',
    status: 'PUBLISHED',
    featured: true,
    createdBy: 'Editorial Team',
    reviewedBy: 'Context Editor',
    publishedAt: '2026-09-10T00:00:00Z',
    createdAt: '2026-08-25T10:00:00Z',
    updatedAt: '2026-09-10T12:00:00Z',
    scriptureIds: ['sc-5', 'sc-6'],
    topicSlugs: ['jewish-context', 'hebrew-concepts', 'torah'],
    readTimeMinutes: 3,
  },
  {
    id: 'idea-4',
    title: 'The Good Shepherd & Covenant Care',
    slug: 'the-good-shepherd',
    category: 'Messiah',
    hook: 'When David proclaimed "Adonai is my shepherd," he invoked Israel’s deepest royal and covenantal imagery.',
    summary: 'Shepherd imagery is woven from Jacob’s blessing of Joseph, through Moses tending Jethro’s flock, to David the shepherd-king. Ezekiel 34 prophesied that God Himself would seek out His scattered sheep through a singular Davidic Shepherd. Yeshua directly claimed this title in John 10.',
    content: 'Psalm 23 expresses complete covenant reliance: lack of want, green pastures, still waters, and paths of righteousness. But this shepherd is also a protector with a rod and staff against predators in the valley of the shadow of death.',
    context: 'In Middle Eastern antiquity, sheep recognized their specific shepherd’s voice and melody. Different flocks would mingle at the well, yet when one shepherd called, only his sheep detached themselves from the multitude to follow him.',
    interpretation: 'By declaring "I am the Good Shepherd" who lays down His life for the sheep, Yeshua identified Himself as the divine shepherd of Ezekiel 34 who gathers both the lost sheep of the house of Israel and other sheep not of this fold.',
    application: 'Covenant security does not mean we will never enter dark valleys; it means we will never walk them alone. The Shepherd leads from the front and guards from the rear.',
    status: 'PUBLISHED',
    featured: false,
    createdBy: 'Editorial Team',
    publishedAt: '2026-09-12T00:00:00Z',
    createdAt: '2026-08-28T10:00:00Z',
    updatedAt: '2026-09-12T12:00:00Z',
    scriptureIds: ['sc-7', 'sc-8'],
    topicSlugs: ['messiah', 'covenant', 'prayer'],
    readTimeMinutes: 2,
  },
  {
    id: 'idea-5',
    title: 'Torah Written on the Heart: The New Covenant',
    slug: 'torah-on-the-heart',
    category: 'Torah',
    hook: 'The New Covenant did not abolish God’s instructions; it relocated them from cold stone tablets to living human hearts.',
    summary: 'Jeremiah 31 explicitly defines the Brit Chadashah (New Covenant): God writes the Torah upon the inner conscience through the Ruach HaKodesh (Holy Spirit), granting the spiritual capacity and desire to walk in His ways.',
    content: 'Many misunderstand the New Covenant as the eradication of biblical ethics or identity. However, Jeremiah 31:33 states: "I will put my Torah within them, and I will write it on their hearts." The content of the instruction remains God’s righteous revelation; what transforms is human empowerment and intimate relationship.',
    context: 'In Jewish thought, Ezekiel 36:26–27 complements Jeremiah: God removes the heart of stone and gives a heart of flesh, causing believers to walk in His statutes through His indwelling Spirit.',
    interpretation: 'Yeshua did not come to destroy the Torah or the Prophets, but to fill them to the full (Matthew 5:17). The New Covenant provides internal regeneration so that obedience flows from covenant love rather than external legal coercion.',
    application: 'Seek the guidance of the Holy Spirit to let God\'s Word transform your inner motivations, speech, business dealings, and family relationships.',
    status: 'PUBLISHED',
    featured: false,
    createdBy: 'Editorial Team',
    publishedAt: '2026-09-14T00:00:00Z',
    createdAt: '2026-08-30T10:00:00Z',
    updatedAt: '2026-09-14T12:00:00Z',
    scriptureIds: ['sc-4'],
    topicSlugs: ['torah', 'covenant', 'prophecy'],
    readTimeMinutes: 3,
  },
  {
    id: 'idea-6',
    title: 'Shabbat: A Foretaste of the Messianic Age',
    slug: 'shabbat-delight',
    category: 'Sabbath',
    hook: 'Sabbath is not a list of restrictions; it is an island of sacred rest in the turbulent ocean of human striving.',
    summary: 'The Sabbath was instituted at Creation, confirmed in the Decalogue, and celebrated as a perpetual sign between God and His people. It offers a weekly foretaste of Olam HaBa (the World to Come) and the ultimate rest found in Messiah.',
    content: 'Genesis 2 records that God rested on the seventh day — not from exhaustion, but to crown creation with tranquility (Menuchah). The Sabbath teaches that our worth is not measured by relentless economic productivity, but by our divine sonship and covenant security.',
    context: 'In 1st-century Jewish practice, the Sabbath began at sundown Friday with candlelight, the blessing of children (Birkat HaBanim), and singing "Shalom Aleichem" to welcome the angels of peace.',
    interpretation: 'Hebrews 4:9 declares that "there remains a Sabbath rest for the people of God." Yeshua declared Himself "Lord of the Sabbath" who brings deep spiritual rest to all who are weary and burdened.',
    application: 'Set apart intentional sacred time to disconnect from digital noise, reflect on God’s goodness with family and community, and rest in Messiah’s completed work.',
    status: 'PUBLISHED',
    featured: false,
    createdBy: 'Editorial Team',
    publishedAt: '2026-09-16T00:00:00Z',
    createdAt: '2026-09-02T10:00:00Z',
    updatedAt: '2026-09-16T12:00:00Z',
    scriptureIds: ['sc-9'],
    topicSlugs: ['sabbath', 'covenant', 'hebrew-concepts'],
    readTimeMinutes: 3,
  },
  {
    id: 'idea-7',
    title: 'The Shema: Allegiance, Love, and Oneness',
    slug: 'the-shema-allegiance',
    category: 'Prayer',
    hook: 'Recited twice daily for millennia, the Shema is Israel’s pledge of exclusive covenant allegiance to the One True God.',
    summary: 'Deuteronomy 6:4 begins: "Shema Yisrael, Adonai Eloheinu, Adonai Echad." When asked what commandment is foremost, Yeshua quoted this exact passage, anchoring His disciples in the unified worship of the God of Abraham, Isaac, and Jacob.',
    content: '"Shema" in Hebrew does not merely mean acoustic hearing; it implies hearing with attentive listening and active obedience. "Echad" speaks of the unique, undivided oneness and sovereignty of God over heaven and earth.',
    context: 'The Mezuzah affixed to Jewish doorposts contains this text handwritten on parchment, constantly reminding entering and departing family members that their household belongs to Adonai.',
    interpretation: 'A Messianic view highlights that Yeshua reaffirmed the Shema as the foundational pillar of the Kingdom of God (Mark 12:29), calling His followers to undivided love with all heart, soul, and resources.',
    application: 'Start and end your day by declaring your allegiance to God, surrendering your heart, intellect, and physical strength to His kingdom purposes.',
    status: 'PUBLISHED',
    featured: false,
    createdBy: 'Editorial Team',
    publishedAt: '2026-09-18T00:00:00Z',
    createdAt: '2026-09-04T10:00:00Z',
    updatedAt: '2026-09-18T12:00:00Z',
    scriptureIds: ['sc-12'],
    topicSlugs: ['prayer', 'jewish-context', 'torah'],
    readTimeMinutes: 2,
  },
  {
    id: 'idea-8',
    title: 'Bethlehem Ephrathah: The Ancient Ruler',
    slug: 'bethlehem-ephrathah',
    category: 'Prophecy',
    hook: 'A humble sheep village on the Judean hills was prophesied to birth the Shepherd whose origins are from days of eternity.',
    summary: 'Micah 5:2 singled out tiny Bethlehem Ephrathah among the thousands of Judah as the birthplace of Israel’s eternal Ruler. When the Magi inquired where the King of the Jews was to be born, the Jerusalem priests cited this exact verse.',
    content: 'Bethlehem means "House of Bread" (Beit Lechem) and Ephrathah means "Fruitfulness." It was the hometown of King David and Ruth. The prophecy remarkably asserts that this coming ruler’s "comings forth are from of old, from ancient days (Mikedem, mimei olam)."',
    context: 'The surrounding fields of Migdal Eder (Tower of the Flock) outside Bethlehem were specifically used to raise unblemished lambs designated for Temple sacrifices in Jerusalem.',
    interpretation: 'Yeshua, the "Bread of Life" born in the "House of Bread," arrived in humility according to ancient prophetic precision, fulfilling both the royal Davidic heritage and the priestly sacrificial shadow.',
    application: 'God consistently chooses the humble, seemingly insignificant things of this world to confound the proud and fulfill His grandest redemptive promises.',
    status: 'PUBLISHED',
    featured: false,
    createdBy: 'Editorial Team',
    publishedAt: '2026-09-20T00:00:00Z',
    createdAt: '2026-09-05T10:00:00Z',
    updatedAt: '2026-09-20T12:00:00Z',
    scriptureIds: ['sc-10'],
    topicSlugs: ['prophecy', 'messiah', 'kingdom'],
    readTimeMinutes: 3,
  }
];

export const INITIAL_STUDY_PATHS: StudyPath[] = [
  {
    id: 'sp-1',
    title: 'Understanding the Messiah',
    slug: 'understanding-the-messiah',
    description: 'A 9-step guided journey through the Tanakh and Apostolic Scriptures exploring kingship, suffering, and Yeshua’s fulfillment.',
    difficulty: 'Intermediate',
    estimatedMinutes: 45,
    published: true,
    lessons: [
      {
        id: 'less-1',
        studyPathId: 'sp-1',
        lessonOrder: 1,
        title: 'What does "Messiah" mean?',
        description: 'Explore the Hebrew concept of Mashiach — the anointed king, priest, and prophet consecrated for divine purpose.',
        estimatedMinutes: 5,
        scriptureReference: '1 Samuel 16:13; Psalm 2:2',
        scriptureText: 'Then Samuel took the horn of oil and anointed him in the midst of his brothers. And the Spirit of the LORD rushed upon David from that day forward.',
        coreIdea: 'The term "Mashiach" comes from the verb mashach (to smear or anoint with oil). Anointing signified divine authorization and empowerment by the Spirit.',
        contextNote: 'In the ancient Near East, kings were crowned by pouring scented oil over the head. In Israel, this was not merely political coronation, but a sacred covenant induction.',
        messianicConnection: 'Yeshua is not "Jesus Christ" as a surname; "Christ" is the Greek translation (Christos) of Mashiach. He is the Ultimate Anointed King.',
        reflectionPrompt: 'How does viewing Yeshua as the Anointed King change the way you relate to His authority in your daily routine?',
      },
      {
        id: 'less-2',
        studyPathId: 'sp-1',
        lessonOrder: 2,
        title: 'The Davidic Promise',
        description: 'The unconditional covenant made with King David regarding an eternal throne and royal dynasty.',
        estimatedMinutes: 5,
        scriptureReference: '2 Samuel 7:12–16',
        scriptureText: 'I will raise up your offspring after you... and I will establish the throne of his kingdom forever.',
        coreIdea: 'God promised David a perpetual dynasty. Even when earthly kings failed, the promise remained steadfast, pointing toward an eternal King.',
        contextNote: 'During the Babylonian exile, when the Davidic monarchy collapsed, Jewish prophets reaffirmed that the "Branch of David" would still arise.',
        messianicConnection: 'Both Matthew 1 and Luke 3 meticulously trace Yeshua’s lineage back through David, confirming His legal and genealogical right to the throne.',
        reflectionPrompt: 'Why is God’s faithfulness to His covenant with David an anchor for our trust in His promises to us?',
      },
      {
        id: 'less-3',
        studyPathId: 'sp-1',
        lessonOrder: 3,
        title: 'Messiah in the Psalms',
        description: 'Discovering the royal and suffering psalms that depict the drama of God’s Anointed.',
        estimatedMinutes: 5,
        scriptureReference: 'Psalm 2:7–8; Psalm 110:1',
        scriptureText: 'The LORD says to my Lord: "Sit at my right hand, until I make your enemies your footstool."',
        coreIdea: 'The Book of Psalms (Tehillim) is saturated with prophetic prayers of the king undergoing rejection before vindication.',
        contextNote: 'Psalm 110 is the most quoted Old Testament passage in the Apostolic Scriptures, demonstrating Messiah’s heavenly session.',
        messianicConnection: 'Yeshua silenced the Pharisees in Matthew 22 by asking: "If David calls him Lord, how is he his son?" pointing to His divine preeminence.',
        reflectionPrompt: 'When praying the Psalms, how can you see both the sufferings and victories of Messiah reflected in your own walk?',
      },
      {
        id: 'less-4',
        studyPathId: 'sp-1',
        lessonOrder: 4,
        title: 'Isaiah’s Servant Passages',
        description: 'Tracking the four Servant Songs of Isaiah and the mission of Adonai’s chosen servant.',
        estimatedMinutes: 5,
        scriptureReference: 'Isaiah 42:1–4; Isaiah 49:6',
        scriptureText: 'I will make you as a light for the nations, that my salvation may reach to the end of the earth.',
        coreIdea: 'The Servant has a dual mission: to restore the preserved of Israel and to be a light to the Gentiles (Goyim).',
        contextNote: 'While corporate Israel is called a servant, Isaiah introduces an individual Servant who redeems unfaithful Israel itself.',
        messianicConnection: 'Through Yeshua, millions from every tribe and nation worship the God of Israel today, exactly as Isaiah foretold.',
        reflectionPrompt: 'In what ways are you experiencing Messiah as a light in your life and sharing that light with others?',
      },
      {
        id: 'less-5',
        studyPathId: 'sp-1',
        lessonOrder: 5,
        title: 'The Suffering Servant',
        description: 'A deep reading of Isaiah 53, the guilt offering, and vicarious atonement.',
        estimatedMinutes: 6,
        scriptureReference: 'Isaiah 53:10–12',
        scriptureText: 'Yet it was the will of the LORD to crush him; he has put him to grief; when his soul makes an offering for guilt, he shall see his offspring.',
        coreIdea: 'The Servant willingly offers His life as an Asham (sacrificial restitution) to bear the sins of the many.',
        contextNote: 'Ancient rabbinic literature in Midrash Tanchuma and the Talmud acknowledged this suffering figure as the Messiah.',
        messianicConnection: 'Yeshua applied Isaiah 53 to Himself at the Last Supper: "For I tell you that this Scripture must be fulfilled in me: And he was numbered with the transgressors."',
        reflectionPrompt: 'What does it mean to you personally that Messiah was wounded for your specific transgressions?',
      },
      {
        id: 'less-6',
        studyPathId: 'sp-1',
        lessonOrder: 6,
        title: 'The Kingdom of God',
        description: 'The arrival of the Malkhut Shamayim — the reign of heaven in human history.',
        estimatedMinutes: 5,
        scriptureReference: 'Daniel 7:13–14',
        scriptureText: 'And behold, with the clouds of heaven there came one like a son of man... And to him was given dominion and glory and a kingdom.',
        coreIdea: 'The Kingdom is both "already" here in spiritual reality and "not yet" fully manifested in visible geopolitical triumph.',
        contextNote: '1st-century Jews expected the Messiah to immediately cast off the Roman yoke; Yeshua revealed that the heart must first be liberated from sin.',
        messianicConnection: 'Yeshua\'s favorite self-designation was "Son of Man," directly linking His identity to the celestial figure in Daniel 7.',
        reflectionPrompt: 'How can you live as an active citizen of God\'s kingdom amid current cultural pressures?',
      },
      {
        id: 'less-7',
        studyPathId: 'sp-1',
        lessonOrder: 7,
        title: 'Yeshua’s Claims',
        description: 'Examining the authority, signs, and identity Yeshua revealed in the Land of Israel.',
        estimatedMinutes: 5,
        scriptureReference: 'Mark 14:61–62',
        scriptureText: 'The high priest asked him, "Are you the Messiah, the Son of the Blessed?" And Yeshua said, "I am."',
        coreIdea: 'Yeshua did not merely teach wise ethics; He claimed the divine prerogatives to forgive sins, command the wind, and judge mankind.',
        contextNote: 'Under Second Temple Jewish jurisprudence, claiming to sit at the right hand of Power was recognized as claiming divine status.',
        messianicConnection: 'His miracles were specific messianic signposts: cleansing the leper, opening the eyes of the blind from birth, and raising the dead.',
        reflectionPrompt: 'Who do you say that Yeshua is when talking with family, colleagues, or friends?',
      },
      {
        id: 'less-8',
        studyPathId: 'sp-1',
        lessonOrder: 8,
        title: 'Resurrection & Firstfruits',
        description: 'The victory over death linked to the biblical feast of Yom HaBikkurim.',
        estimatedMinutes: 5,
        scriptureReference: '1 Corinthians 15:20–23',
        scriptureText: 'But in fact Messiah has been raised from the dead, the firstfruits of those who have fallen asleep.',
        coreIdea: 'The resurrection was not a resuscitation back to mortal weakness, but the inaugural breakthrough of the new creation.',
        contextNote: 'The Feast of Firstfruits occurred on the morning after the Sabbath during Passover week — the exact day Yeshua rose.',
        messianicConnection: 'Because the Firstfruits guarantee the rest of the harvest, Messiah’s resurrection guarantees the future resurrection of all believers.',
        reflectionPrompt: 'How does the living reality of the resurrection grant you courage when facing grief or mortality?',
      },
      {
        id: 'less-9',
        studyPathId: 'sp-1',
        lessonOrder: 9,
        title: 'The Messiah and the Future',
        description: 'The return to Zion, the restoration of Israel, and peace among the nations.',
        estimatedMinutes: 5,
        scriptureReference: 'Zechariah 14:9; Revelation 21:1–4',
        scriptureText: 'And the LORD will be king over all the earth. On that day the LORD will be one and his name one.',
        coreIdea: 'History is not cyclical futility; it has an appointed destination when justice, peace, and God’s dwelling will fill the cosmos.',
        contextNote: 'Zechariah 14 foresees all nations ascending to Jerusalem year after year to worship the King and celebrate the Feast of Tabernacles (Sukkot).',
        messianicConnection: 'Yeshua reigns as the Prince of Peace (Sar Shalom) and will wipe away every tear from every eye in the New Jerusalem.',
        reflectionPrompt: 'How does looking forward to Messiah’s return inspire patience and holy living today?',
      }
    ]
  },
  {
    id: 'sp-2',
    title: 'The Biblical Feasts & Messiah',
    slug: 'the-biblical-feasts',
    description: 'Discover how the 7 Appointed Times of Leviticus 23 form God’s prophetic calendar of redemption.',
    difficulty: 'Beginner',
    estimatedMinutes: 35,
    published: true,
    lessons: [
      {
        id: 'less-201',
        studyPathId: 'sp-2',
        lessonOrder: 1,
        title: 'The Moedim: God’s Rehearsals',
        description: 'Understanding what makes the feasts "divine appointments" rather than human holidays.',
        estimatedMinutes: 5,
        scriptureReference: 'Leviticus 23:1–4',
        scriptureText: 'These are the appointed feasts of the LORD... which you shall proclaim as holy convocations.',
        coreIdea: 'The Hebrew word "Moed" means an appointed time or meeting. "Mikra" means a dress rehearsal for prophetic events.',
        contextNote: 'The biblical year is structured around the agricultural harvest cycles in the Land of Israel, illustrating spiritual harvests.',
        messianicConnection: 'Colossians 2:17 explains that these festivals are shadows of things to come, whose substance belongs to Messiah.',
        reflectionPrompt: 'Have you ever considered that the biblical calendar has prophetic significance for our lives today?',
      },
      {
        id: 'less-202',
        studyPathId: 'sp-2',
        lessonOrder: 2,
        title: 'Pesach (Passover): The Lamb',
        description: 'Deliverance from bondage through the blood on the doorposts.',
        estimatedMinutes: 5,
        scriptureReference: 'Exodus 12:1–14; John 1:29',
        scriptureText: 'Behold, the Lamb of God, who takes away the sin of the world!',
        coreIdea: 'Passover celebrates physical redemption from Pharaoh and spiritual redemption from the servitude of sin.',
        contextNote: 'The Passover lamb was examined from the 10th to the 14th of Nisan for blemishes — during which time Yeshua was questioned in the Temple.',
        messianicConnection: 'Yeshua instituted the Lord’s Supper using the elements of the Passover Seder: the unleavened bread and the third cup (Cup of Redemption).',
        reflectionPrompt: 'In what areas has Messiah brought you out of your personal "Egypt"?',
      }
    ]
  }
];

export const INITIAL_KNOWLEDGE_GRAPH: KnowledgeGraphData = {
  centralNode: 'node-passover',
  nodes: [
    { id: 'node-passover', label: 'PASSOVER', hebrew: 'פֶּסַח', type: 'feast', description: 'The festival of freedom and divine redemption in the month of Nisan.', scriptureRef: 'Exodus 12' },
    { id: 'node-lamb', label: 'THE LAMB', hebrew: 'שֶׂה', type: 'scripture', description: 'Spotless male yearling whose blood marks the doorpost.', scriptureRef: 'Exodus 12:5' },
    { id: 'node-redemption', label: 'REDEMPTION', hebrew: 'גְּאֻלָּה', type: 'concept', description: 'Deliverance from slavery into freedom and covenant relationship.', scriptureRef: 'Leviticus 25' },
    { id: 'node-blood', label: 'THE BLOOD', hebrew: 'דָּם', type: 'scripture', description: 'The life of the flesh is in the blood given upon the altar.', scriptureRef: 'Leviticus 17:11' },
    { id: 'node-exodus', label: 'THE EXODUS', hebrew: 'יְצִיאַת מִצְרַיִם', type: 'event', description: 'Adonai brings Israel out of Egypt with a mighty hand and outstretched arm.', scriptureRef: 'Exodus 13' },
    { id: 'node-covenant', label: 'COVENANT', hebrew: 'בְּרִית', type: 'concept', description: 'The binding mutual promise established between God and His people.', scriptureRef: 'Genesis 15; Exodus 24' },
    { id: 'node-messiah', label: 'YESHUA / MESSIAH', hebrew: 'יֵשׁוּעַ הַמָּשִׁיחַ', type: 'messianic', description: 'The Son of God, our Passover Lamb sacrificed for us.', scriptureRef: '1 Corinthians 5:7' },
    { id: 'node-last-supper', label: 'THE LAST SEDER', hebrew: 'סְעוּדָה אַחֲרוֹנָה', type: 'event', description: 'Yeshua breaks the matzah and blesses the third cup of redemption.', scriptureRef: 'Luke 22:14–20' },
    { id: 'node-isaiah53', label: 'SUFFERING SERVANT', hebrew: 'עֶבֶד הַשֵּׁם', type: 'messianic', description: 'Led like a lamb to the slaughter, opening not his mouth.', scriptureRef: 'Isaiah 53:7' },
    { id: 'node-firstfruits', label: 'FIRSTFRUITS', hebrew: 'בִּכּוּרִים', type: 'feast', description: 'Resurrection harvest presented to God on the morrow after Shabbat.', scriptureRef: 'Leviticus 23:10; 1 Cor 15:20' }
  ],
  edges: [
    { from: 'node-passover', to: 'node-lamb', relationship: 'Sacrificial Element' },
    { from: 'node-passover', to: 'node-exodus', relationship: 'Historical Origin' },
    { from: 'node-passover', to: 'node-redemption', relationship: 'Core Meaning' },
    { from: 'node-lamb', to: 'node-blood', relationship: 'Atonement Sign' },
    { from: 'node-blood', to: 'node-covenant', relationship: 'Ratification' },
    { from: 'node-lamb', to: 'node-messiah', relationship: 'Fulfilled In' },
    { from: 'node-messiah', to: 'node-last-supper', relationship: 'Memorialized In' },
    { from: 'node-messiah', to: 'node-isaiah53', relationship: 'Prophesied As' },
    { from: 'node-passover', to: 'node-firstfruits', relationship: 'Sequential Moed' },
    { from: 'node-firstfruits', to: 'node-messiah', relationship: 'Resurrection Day' }
  ]
};
